import { Pipe, PipeTransform } from '@angular/core';

export type VideoType = 'vimeo' | 'youtube' | 'unknown';
export type VideoSettings = {
    type: VideoType;
    id: string;
};

const checkVideoType = (source: string | null): VideoType => {
    if (source?.includes('vimeo')) {
        return 'vimeo';
    }

    if (source?.includes('youtube')) {
        return 'youtube';
    }

    return 'unknown';
};

/**
 * This pipe will check if the source is a valid video url from vimeo or youtube and return the type and the id
 * @example
 * (https://www.youtube.com/watch?v=3x1sad43) | typeOfVideo => { type: 'youtube', id: '3x1sad43' }
 * (https://vimeo/31214123) | typeOfVideo => { type: 'vimeo', id: '31214123' }
 */
@Pipe({
    name: 'typeOfVideo',
    standalone: true
})
export class TypeOfVideoPipe implements PipeTransform {
    transform(source: string): VideoSettings {
        // let's check if the soruce it's an url from vimeo or youtube
        const videoUrlRegex =
            /(?:http(?:s)?:\/\/)?(?:player.|www\.)?(vimeo\.com|youtu(?:be\.com|\.be|be))\/(?:media\/embed\/|video\/watch\/|video\/|embed\/|watch\?v=|v\/)?(?:(?:view|ll_embed)\?)?(?:(f|i)=)?(?:video\/|(?:.*-))?([A-Za-z0-9._%-]*)(?:\&\S+)?/;
        const match = source.match(videoUrlRegex);
        let type: VideoType = checkVideoType(match ? match[1] : null);
        let id = match ? match[3] : '';

        // if the id or the video type is not found, we will check if the source is a valid id already
        if (type === 'unknown' || !id) {
            // yeah, we are checking if the source is a valid id because the Vimeo ID is a number
            // This is not flawless and I don't like it
            // but this is only used in case of the input is not an url
            const isYoutubeId = isNaN(Number(source));
            type = (isYoutubeId ? 'youtube' : 'vimeo') as VideoType;
            id = source.trim();
        }

        // after all the checks, if the type is still unknown, we will log a warning
        if (type === 'unknown' || !id) {
            console.warn('The video type is unknown');
        }

        return { type, id };
    }
}
